import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader } from 'lucide-react';

import AuthScreen from './components/AuthScreen';
import Landing from './components/Landing';
import DocumentForm from './components/DocumentForm';
import DocumentList from './components/DocumentList';
import UserProfile from './components/UserProfile';
import LoadingScreen from './components/LoadingScreen';
import AnimatedBadge from './components/AnimatedBadge';
import './styles/App.css';

// Build a safe API base URL. In production we prefer relative `/api`.
const rawApi = process.env.REACT_APP_API_URL || '';
const API_URL = (rawApi || '').replace(/\/+$/, '');

function App() {
  const normalizePath = (p) => (p || '/').replace(/\/+$/, '') || '/';
  const [route, setRoute] = useState(normalizePath(window.location.pathname)); // '/', '/login', '/application'
  const [step, setStep] = useState('form'); // 'form' | 'checklist' for /application
  const [user, setUser] = useState(null);
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [uploadingDocs, setUploadingDocs] = useState({});

  const navigate = (path) => {
    const p = normalizePath(path);
    if (p !== route) {
      window.history.pushState({}, '', p);
      setRoute(p);
    }
  };

  useEffect(() => {
    const onPop = () => setRoute(normalizePath(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Always open pages at top (disable scroll restoration)
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  // Scroll to top when route or application step changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [route, step]);

  // Check for existing authentication
  useEffect(() => {
    const checkAuth = async () => {
      setLoading(true);
      
      // Check for URL token (from Google OAuth)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('token');
      
      // Check for stored token
      const storedToken = localStorage.getItem('authToken');
      const token = urlToken || storedToken;
      
      if (token) {
        try {
          const response = await axios.post(`${API_URL}/api/auth/verify`, { token });
          const userData = response.data.user;
          
          setUser(userData);
          setAuthToken(token);
          localStorage.setItem('authToken', token);
          
          // Clean up URL
          if (urlToken) {
            window.history.replaceState({}, document.title, '/');
          }

          // If user upgraded from guest, migrate their data
          const pendingMigration = localStorage.getItem('pendingMigration');
          if (pendingMigration) {
            try {
              await axios.post(`${API_URL}/api/auth/migrate`, {
                guestUserId: pendingMigration,
                token
              });
              localStorage.removeItem('pendingMigration');
              // Keep using the guest application's ID for loading
              localStorage.setItem('visaAppUserId', pendingMigration);
            } catch (e) {
              console.error('Migration failed:', e);
            }
          }

          // If user explicitly chose to start a new application, show empty form
          const forceNew = localStorage.getItem('startNewApplication') === '1';
          if (forceNew) {
            setStep('form');
            navigate('/application');
          } else {
            // Prefer loading existing application by locally saved guest ID
            const savedUserId = localStorage.getItem('visaAppUserId');
            if (savedUserId) {
              await loadApplication(savedUserId);
              navigate('/application');
            } else if (userData?.id) {
              // Try to load by real Google user id (linked on server)
              await loadApplication(userData.id);
              navigate('/application');
            } else {
              // No existing application — go to form to start one
              setStep('form');
              navigate('/application');
            }
          }
          
        } catch (error) {
          console.error('Auth verification failed:', error);
          localStorage.removeItem('authToken');
          if (route === '/application') navigate('/login');
        }
      } else {
        // Not authenticated: try to restore guest, else show landing or new form
        const forceNew = localStorage.getItem('startNewApplication') === '1';
        const savedUserId = localStorage.getItem('visaAppUserId');
        if (savedUserId && !forceNew) {
          await loadApplication(savedUserId);
          setStep('checklist');
          navigate('/application');
        } else if (route === '/application') {
          setStep('form');
        } else {
          if (route === '/application') navigate('/login');
        }
      }
      
      setLoading(false);
    };

    checkAuth();
  }, []);

  const loadApplication = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/application/${userId}`);
      setApplication(response.data.application);
      // Load checklist from server or fallback to local cache
      const serverChecklist = response.data.checklist || [];
      if (serverChecklist && serverChecklist.length > 0) {
        setDocuments(serverChecklist);
      } else {
        try {
          const cached = localStorage.getItem(`visaChecklist:${userId}`);
          if (cached) setDocuments(JSON.parse(cached));
        } catch (e) {
          // ignore cache errors
        }
      }
      // Build map of latest uploaded doc per type (API returns newest first)
      const latestByType = {};
      for (const doc of response.data.documents) {
        if (!latestByType[doc.document_type]) {
          latestByType[doc.document_type] = doc;
        }
      }
      setUploadedDocs(latestByType);
      setStep('checklist');
    } catch (error) {
      console.error('Error loading application:', error);
      // If not found or error, go to form to start a new one
      setStep('form');
    }
  };

  const handleFormSubmit = async (formData) => {
    setLoading(true);
    
    try {
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const response = await axios.post(`${API_URL}/api/generate-checklist`, formData, { headers });
      
      setApplication({
        id: response.data.applicationId,
        userId: response.data.userId
      });
      setDocuments(response.data.documents);
      
      localStorage.setItem('visaAppUserId', response.data.userId);
      // Clear new-app intent flag once created
      localStorage.removeItem('startNewApplication');
      // Cache checklist locally for guests/fallbacks
      try {
        localStorage.setItem(`visaChecklist:${response.data.userId}`, JSON.stringify(response.data.documents));
      } catch (e) {
        console.warn('Failed to cache checklist locally', e);
      }
      setStep('checklist');
    } catch (error) {
      console.error('Error generating checklist:', error);
      alert('Failed to generate checklist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (docType, file) => {
    if (!file) return;
    
    // Show loading indicator for this document
    setUploadingDocs(prev => ({ ...prev, [docType]: true }));
    
    const formData = new FormData();
    formData.append('document', file);
    formData.append('applicationId', application.id);
    formData.append('documentType', docType);
    
    try {
      const response = await axios.post(
        `${API_URL}/api/upload-document`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      const savedDoc = response.data.document || {
        file_url: response.data.fileUrl,
        document_type: docType,
        ai_feedback: response.data.analysis
      };
      setUploadedDocs(prev => ({
        ...prev,
        [docType]: savedDoc
      }));
    } catch (error) {
      let msg = error?.response?.data?.error || error.message || 'Failed to upload document.';
      const detail = error?.response?.data?.detail;
      if (detail) msg += ` (${detail})`;
      console.error('Upload error:', error?.response?.data || error);
      alert(`Failed to upload document: ${msg}`);
    } finally {
      // Remove loading indicator
      setUploadingDocs(prev => ({ ...prev, [docType]: false }));
    }
  };

  const generateLetter = async (type) => {
    try {
      const response = await axios.post(`${API_URL}/api/generate-letter`, {
        type,
        applicationId: application.id,
        context: 'Standard visa application'
      });
      
      // Create downloadable file
      const blob = new Blob([response.data.letter], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type.replace(' ', '_')}.txt`;
      a.click();
    } catch (error) {
      console.error('Error generating letter:', error);
    }
  };

  const downloadAll = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/download-all/${application.id}`);
      alert(`${response.data.documents.length} documents ready for download`);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  // Authentication handlers
  const handleGoogleLogin = () => {
    window.location.href = `${API_URL}/api/auth/google`;
  };

  const handleGuestLogin = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/api/auth/guest`);
      
      setUser(response.data.user);
      setAuthToken(response.data.token);
      localStorage.setItem('authToken', response.data.token);
      
      setStep('form');
      navigate('/application');
      setLoading(false);
    } catch (error) {
      console.error('Guest login failed:', error);
      alert('Failed to create a guest session');
      setLoading(false);
    }
  };

  const handleUpgradeToGoogle = () => {
    // Save current guest ID for migration
    if (user?.tempId) {
      localStorage.setItem('pendingMigration', user.tempId);
    }
    handleGoogleLogin();
  };

  const handleLogout = () => {
    setUser(null);
    setAuthToken(null);
    setApplication(null);
    setDocuments([]);
    setUploadedDocs({});
    localStorage.removeItem('authToken');
    localStorage.removeItem('visaAppUserId');
    localStorage.removeItem('startNewApplication');
    navigate('/login');
  };

  const startNewApplication = () => {
    setStep('form');
    setApplication(null);
    setDocuments([]);
    setUploadedDocs({});
    // Mark intent to create a fresh application, so reloads keep the form
    localStorage.setItem('startNewApplication', '1');
    // Clear cached checklist for previous guest id
    try {
      const prevId = localStorage.getItem('visaAppUserId');
      if (prevId) localStorage.removeItem(`visaChecklist:${prevId}`);
    } catch (e) {}
    localStorage.removeItem('visaAppUserId');
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="app">
      {route === '/' ? (
        <Landing onGetStarted={() => navigate('/login')} />
      ) : route === '/login' ? (
        <AuthScreen onGoogleLogin={handleGoogleLogin} onGuestLogin={handleGuestLogin} />
      ) : route === '/application' ? (
        step === 'form' ? (
          <div className="container">
            <div className="page-badge"><AnimatedBadge size={60} /></div>
            <div className="card">
              <div className="header">
                <h1>Visa Document Assistant</h1>
                <UserProfile user={user} onLogout={handleLogout} onUpgradeToGoogle={handleUpgradeToGoogle} />
              </div>
              <DocumentForm onSubmit={handleFormSubmit} />
            </div>
          </div>
        ) : (
          <div className="container">
            <div className="page-badge"><AnimatedBadge size={60} /></div>
            <div className="card">
              <div className="header">
                <h1>Your Documents</h1>
                <UserProfile user={user} onLogout={handleLogout} onUpgradeToGoogle={handleUpgradeToGoogle} />
              </div>
              <DocumentList
                documents={documents}
                uploadedDocs={uploadedDocs}
                uploadingDocs={uploadingDocs}
                onFileUpload={handleFileUpload}
                onGenerateLetter={generateLetter}
                onDownloadAll={downloadAll}
                onStartNew={startNewApplication}
              />
            </div>
          </div>
        )
      ) : null}
    </div>
  );
}

export default App;
